import { AnyTheme } from 'base/components/GenericSlot';
import { prose, typography } from 'modules/tailwind/theme.config';
import { extendTailwindMerge, fromTheme, twJoin } from 'tailwind-merge';
import { createTV } from 'tailwind-variants';
import { isNotNull, isObject, isString } from 'typesafe-utils';

type ExtraClassGroups = 'prose' | 'typography' | 'wings';
type ExtraThemeGroups = never;

// Extracted from tailwind-merge types
type ClassNameValue = ClassNameArray | string | null | undefined | 0 | 0n | false;
type ClassNameArray = ClassNameValue[];

const isCommonProse = (classPart: string) => {
  return /(coverbox|factbox)/.test(classPart);
};

const isCommonTypograph = (classPart: string) => {
  return /(headline|body|preamble|blockquote|input|quote|content)-?\d?(xs|sm|md|lg|xl)?/.test(classPart);
};

const withGridSystem = {
  ...['max-w', 'min-w', 'w'].reduce((result, key) => {
    return {
      ...result,
      [key]: [
        {
          [key]: [
            'content',
            'article',
            'grid',
            'cols-1',
            'cols-2',
            'cols-3',
            'cols-4',
            'cols-5',
            'cols-6',
            'cols-7',
            'cols-8',
            'cols-9',
            'cols-10',
            'cols-11',
            'cols-12',
          ],
        },
      ],
    };
  }, {} as any),
  ...['m', 'ml', 'mr', 'mt', 'mb', 'mx', 'my', 'p', 'pl', 'pr', 'pt', 'pb', 'px', 'py', 'gap', 'gap-x', 'gap-y'].reduce(
    (result, key) => {
      return {
        ...result,
        [key]: [{ [key]: ['grid', 'grid-1/2', 'grid-m', 'grid-m-1/2', 'grid-gap', 'grid-gap-1/2'] }],
      };
    },
    {} as any,
  ),
};

const twMergeConfig = {
  extend: {
    classGroups: {
      'font-size': [{ text: [isCommonTypograph, ...typography], prose: [isCommonProse, ...prose] }],
      wings: [{ wings: ['', 'none'] }],
      'wings-color': [{ wings: [fromTheme('color'), 'none'] }],
      ...withGridSystem,
    },
  },
};

const customTV = createTV({ twMergeConfig });

const classNameArrayToString = (array: ClassNameArray): string => {
  return array
    .reduce<string[]>((result, value) => {
      if (Array.isArray(value)) {
        result.push(...classNameArrayToString(value));
      }

      if (isString(value)) {
        result.push(value);
      }

      return result;
    }, [] as string[])
    .join(' ');
};

const withClassNameStrings = (theme: AnyTheme): void => {
  if (theme.base) {
    theme.base = deepConvertClassName(theme.base);
  }

  if (theme.slots) {
    theme.slots = deepConvertClassName(theme.slots);
  }

  if (theme.variants) {
    theme.variants = deepConvertClassName(theme.variants);
  }
};

const deepConvertClassName = (value: any) => {
  if (Array.isArray(value)) {
    return classNameArrayToString(value);
  }

  if (isObject(value) && isNotNull(value)) {
    for (const key in value) {
      value[key] = deepConvertClassName(value[key]);
    }
  }

  return value;
};

export const twTheme: typeof customTV = (theme) => {
  withClassNameStrings(theme);

  return customTV(theme);
};

export const twMerge = extendTailwindMerge<ExtraClassGroups, ExtraThemeGroups>(twMergeConfig);

export const tw = {
  theme: twTheme,
  merge: twMerge,
  join: twJoin,
};
